import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, CheckCircle, XCircle, Power, Trash2, MapPin, Calendar, Users, Phone, User, ShieldAlert, Sparkles, Search, Eye, X, MessageSquare, Mail, Award, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_URL as API } from '../../config/api';
import TournamentLeaderboard from '../TournamentLeaderboard';

export default function AdminTournaments({ token, onToast }) {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'approved' | 'inactive' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // Selected Tournament Details Modal State
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const fetchTournaments = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tournaments/owner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournaments(res.data);
    } catch (err) {
      console.error('Failed to fetch admin tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
  }, [token]);

  useEffect(() => {
    if (selectedTournament) {
      setLoadingRegistrations(true);
      axios.get(`${API}/tournaments/${selectedTournament._id}/registrations`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => setRegistrations(res.data || []))
      .catch(err => console.error(err))
      .finally(() => setLoadingRegistrations(false));
    } else {
      setRegistrations([]);
      setSelectedTeam(null);
    }
  }, [selectedTournament, token]);

  const handleApprove = async (id, approveState = true) => {
    setActionLoading(id);
    try {
      await axios.put(`${API}/tournaments/${id}/approve?approve=${approveState}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onToast) onToast(approveState ? 'Tournament Approved!' : 'Tournament status updated', 'success');
      fetchTournaments();
      if (selectedTournament && selectedTournament._id === id) {
        setSelectedTournament(prev => ({ ...prev, is_approved: approveState }));
      }
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to update approval status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = async (t) => {
    const newActive = !t.is_active;
    setActionLoading(t._id);
    try {
      await axios.put(`${API}/tournaments/${t._id}`, { is_active: newActive }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onToast) onToast(newActive ? 'Tournament Activated!' : 'Tournament Deactivated!', 'success');
      fetchTournaments();
      if (selectedTournament && selectedTournament._id === t._id) {
        setSelectedTournament(prev => ({ ...prev, is_active: newActive }));
      }
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to toggle active status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tournament permanently?')) return;
    setActionLoading(id);
    try {
      await axios.delete(`${API}/tournaments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onToast) onToast('Tournament deleted', 'success');
      if (selectedTournament && selectedTournament._id === id) {
        setSelectedTournament(null);
      }
      fetchTournaments();
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to delete tournament');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStatusChange = async (tId, newStatus, targetTournament) => {
    const tourney = targetTournament || selectedTournament || tournaments.find(t => (t._id === tId || t.id === tId));

    if (newStatus === 'cancelled') {
      const createdAtStr = tourney?.created_at;
      if (createdAtStr) {
        const createdAt = new Date(createdAtStr);
        const hoursElapsed = (new Date().getTime() - createdAt.getTime()) / (1000 * 60 * 60);
        if (hoursElapsed > 24) {
          const confirmCancel = window.confirm(
            "⚠️ Warning: This tournament was created more than 24 hours ago. Cancelling it after 24 hours will affect registered teams and players.\n\nAre you sure you want to cancel this tournament?"
          );
          if (!confirmCancel) return;
        }
      }
    }

    setActionLoading(tId);
    try {
      const res = await axios.put(`${API}/tournaments/${tId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onToast) onToast(`Tournament status updated to ${newStatus.toUpperCase()}`, 'success');
      fetchTournaments();
      if (selectedTournament && (selectedTournament._id === tId || selectedTournament.id === tId)) {
        setSelectedTournament(res.data);
      }
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to update tournament status');
    } finally {
      setActionLoading(null);
    }
  };

  const [refundingId, setRefundingId] = useState(null);

  const handleAdminRefund = async (regId, teamName, amount) => {
    const confirmRefund = window.confirm(`Are you sure you want to refund ₹${amount} to team "${teamName}"?`);
    if (!confirmRefund) return;

    setRefundingId(regId);
    try {
      const res = await axios.post(`${API}/tournaments/registrations/${regId}/admin-refund`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onToast) onToast(res.data.msg || 'Refund processed successfully!', 'success');
      if (selectedTournament) {
        const regRes = await axios.get(`${API}/tournaments/${selectedTournament._id || selectedTournament.id}/registrations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRegistrations(regRes.data || []);
      }
    } catch (err) {
      alert(err.response?.data?.detail || err.response?.data?.msg || 'Failed to process refund');
    } finally {
      setRefundingId(null);
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 9;

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery]);

  const pendingCount = tournaments.filter(t => !t.is_approved).length;
  const approvedCount = tournaments.filter(t => t.is_approved && t.is_active).length;
  const inactiveCount = tournaments.filter(t => !t.is_active).length;

  const filteredTournaments = tournaments.filter(t => {
    if (activeTab === 'pending') return !t.is_approved;
    if (activeTab === 'approved') return t.is_approved && t.is_active;
    if (activeTab === 'inactive') return !t.is_active;
    return true;
  }).filter(t => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (t.name || '').toLowerCase().includes(q)
      || (t.turf_name || '').toLowerCase().includes(q)
      || (t.owner_name || '').toLowerCase().includes(q)
      || (t.organizer_name || '').toLowerCase().includes(q)
      || (t.organizer_phone || t.owner_phone || '').includes(q);
  });

  const totalPages = Math.ceil(filteredTournaments.length / ITEMS_PER_PAGE) || 1;
  const paginatedTournaments = filteredTournaments.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#161616', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Trophy color="#084734" size={28} /> Tournament Management
          </h2>
          <p style={{ color: '#374151', fontSize: '0.9rem', margin: '4px 0 0', fontWeight: '500' }}>
            Review partner tournament submissions, view registered players, approve, or manage listings
          </p>
        </div>

        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1.5px solid #EEF2E6', borderRadius: '12px', padding: '8px 14px', width: '280px' }}>
          <Search size={16} color="#6B7280" style={{ marginRight: '8px' }} />
          <input
            placeholder="Search tournament, partner, phone..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.85rem', width: '100%', color: '#161616' }}
          />
        </div>
      </div>

      {/* FILTER TABS */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {[
          { id: 'pending', label: 'Pending Approval', count: pendingCount, color: '#D97706', bg: '#FEF3C7' },
          { id: 'approved', label: 'Approved & Active', count: approvedCount, color: '#084734', bg: '#DCEFB8' },
          { id: 'inactive', label: 'Inactive / Deactivated', count: inactiveCount, color: '#6B7280', bg: '#F3F4F6' },
          { id: 'all', label: 'All Tournaments', count: tournaments.length, color: '#111827', bg: '#F9FAFB' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              borderRadius: '14px',
              border: `1.5px solid ${activeTab === tab.id ? '#084734' : '#EEF2E6'}`,
              background: activeTab === tab.id ? '#084734' : 'white',
              color: activeTab === tab.id ? '#CEF17B' : '#374151',
              fontWeight: '700',
              fontSize: '0.88rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: activeTab === tab.id ? '0 4px 12px rgba(8,71,52,0.15)' : 'none'
            }}
          >
            <span>{tab.label}</span>
            <span style={{
              background: activeTab === tab.id ? 'rgba(206,241,123,0.25)' : tab.bg,
              color: activeTab === tab.id ? '#CEF17B' : tab.color,
              padding: '2px 8px',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: '800'
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* LIST OF TOURNAMENTS */}
      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280', fontWeight: '600' }}>
          Loading tournaments...
        </div>
      ) : paginatedTournaments.length > 0 ? (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
            {paginatedTournaments.map(t => {
            const formatStr = t.tournament_type === 'Custom' ? t.tournament_type_custom : t.tournament_type;
            const sportStr = t.sport === 'Custom' ? t.sport_custom : t.sport;

            return (
              <div
                key={t._id}
                style={{
                  background: 'white', borderRadius: '20px', border: '1.5px solid #EEF2E6',
                  padding: '1.6rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)', opacity: t.is_active ? 1 : 0.8,
                  cursor: 'pointer', transition: 'all 0.2s'
                }}
                onClick={() => setSelectedTournament(t)}
              >
                <div>
                  {/* Badges Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '6px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <span style={{
                        background: t.is_approved ? '#DCEFB8' : '#FEF3C7',
                        color: t.is_approved ? '#084734' : '#D97706',
                        fontSize: '0.72rem', fontWeight: '800', padding: '4px 10px', borderRadius: '20px'
                      }}>
                        {t.is_approved ? '✅ Approved' : '⏳ Pending Approval'}
                      </span>
                      <span style={{
                        background: t.is_active ? '#E0F2FE' : '#F3F4F6',
                        color: t.is_active ? '#0284C7' : '#6B7280',
                        fontSize: '0.72rem', fontWeight: '800', padding: '4px 10px', borderRadius: '20px'
                      }}>
                        {t.is_active ? '🟢 Active' : '⏸️ Inactive'}
                      </span>
                    </div>

                    <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#084734', background: '#F0FDF4', padding: '4px 10px', borderRadius: '20px' }}>
                      {sportStr}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '900', color: '#161616', marginBottom: '10px', lineHeight: 1.3 }}>
                    {t.name}
                  </h3>

                  {/* Partner / Organizer Info Box */}
                  <div style={{ background: '#F8FAF7', padding: '10px 14px', borderRadius: '12px', border: '1px solid #EEF2E6', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Organizer / Partner Info
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', color: '#161616', fontWeight: '700' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <User size={14} color="#084734" /> {t.organizer_name || t.owner_name || 'Partner'}
                      </span>
                      {(t.organizer_phone || t.owner_phone) && (
                        <span style={{ fontSize: '0.8rem', color: '#4B5563', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Phone size={12} /> {t.organizer_phone || t.owner_phone}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Location & Details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.85rem', color: '#4B5563', marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MapPin size={15} color="#084734" />
                      <span style={{ fontWeight: '600', color: '#161616' }}>{t.turf_name}</span> · {t.address}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: '#F8FAF7', padding: '8px 12px', borderRadius: '10px', border: '1px solid #EEF2E6' }}>
                      <div style={{ fontSize: '0.8rem', color: '#161616', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} color="#084734" />
                        <span><strong>Starts From:</strong> {t.start_date ? new Date(t.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'TBD'}</span>
                      </div>
                      {t.registration_close_date && (
                        <div style={{ fontSize: '0.78rem', color: '#DC2626', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '20px' }}>
                          <span><strong>Reg Closes:</strong> {new Date(t.registration_close_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      )}
                      {t.end_date && (
                        <div style={{ fontSize: '0.8rem', color: '#4B5563', display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '20px' }}>
                          <span><strong>Ends On:</strong> {new Date(t.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Users size={15} color="#084734" />
                      <span>Format: <strong>{formatStr}</strong> ({t.team_size}v{t.team_size})</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🎂 <strong>Age Limit:</strong> {t.is_open_age || (!t.min_age && !t.max_age) ? 'Open for All Ages' : `${t.min_age || 0} - ${t.max_age || 'Any'} Years`}</span>
                    </div>
                  </div>

                  {/* Fee & Prize */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FFFBEB', padding: '8px 12px', borderRadius: '10px', marginBottom: '1.25rem' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: '800', color: '#B45309' }}>
                      🏆 {t.prize_pool || 'No Prize Listed'}
                    </span>
                    <span style={{ fontSize: '0.9rem', fontWeight: '900', color: '#084734' }}>
                      {t.entry_fee > 0 ? `₹${t.entry_fee}` : 'FREE'}
                    </span>
                  </div>

                </div>

                {/* ACTION BUTTONS FOR ADMIN */}
                <div
                  style={{ borderTop: '1px solid #EEF2E6', paddingTop: '1rem', display: 'flex', gap: '8px', flexWrap: 'wrap' }}
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    onClick={() => setSelectedTournament(t)}
                    style={{
                      flex: '1 1 100%', background: '#084734', color: '#FFFFFF', border: 'none',
                      padding: '10px 14px', borderRadius: '10px', fontWeight: '800',
                      fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                    }}
                    title="View details and registered teams"
                  >
                    <Eye size={16} /> View Details & Teams
                  </button>

                  {!t.is_approved ? (
                    <button
                      onClick={() => handleApprove(t._id, true)}
                      disabled={actionLoading === t._id}
                      style={{
                        flex: 1, background: '#084734', color: '#CEF17B', border: 'none',
                        padding: '10px 14px', borderRadius: '10px', fontWeight: '800',
                        fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}
                    >
                      <CheckCircle size={16} /> Approve
                    </button>
                  ) : (
                    <button
                      onClick={() => handleApprove(t._id, false)}
                      disabled={actionLoading === t._id}
                      style={{
                        background: '#FEF3C7', color: '#D97706', border: '1px solid #FCD34D',
                        padding: '10px 14px', borderRadius: '10px', fontWeight: '700',
                        fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                      }}
                    >
                      <XCircle size={16} /> Revoke Approval
                    </button>
                  )}

                  {/* Activate / Deactivate button */}
                  <button
                    onClick={() => handleToggleActive(t)}
                    disabled={actionLoading === t._id}
                    style={{
                      background: t.is_active ? '#F3F4F6' : '#E0F2FE',
                      color: t.is_active ? '#374151' : '#0284C7',
                      border: '1px solid #E5E7EB',
                      padding: '10px 14px', borderRadius: '10px', fontWeight: '700',
                      fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                  >
                    <Power size={15} /> {t.is_active ? 'Deactivate' : 'Activate'}
                  </button>

                  {/* Delete button */}
                  <button
                    onClick={() => handleDelete(t._id)}
                    disabled={actionLoading === t._id}
                    style={{
                      background: '#FEE2E2', color: '#DC2626', border: 'none',
                      padding: '10px 14px', borderRadius: '10px', fontWeight: '700',
                      fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                    }}
                    title="Delete tournament"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

          {/* PAGINATION BAR (9 Cards per Page) */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '2.5rem' }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{
                  padding: '10px 18px', borderRadius: '12px', border: '1.5px solid #084734',
                  background: currentPage === 1 ? '#F3F4F6' : 'white',
                  color: currentPage === 1 ? '#9CA3AF' : '#084734',
                  fontWeight: '800', fontSize: '0.85rem', cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s'
                }}
              >
                <ChevronLeft size={18} /> Prev
              </button>

              <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#084734', background: '#DCEFB8', padding: '8px 16px', borderRadius: '12px' }}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{
                  padding: '10px 18px', borderRadius: '12px', border: '1.5px solid #084734',
                  background: currentPage === totalPages ? '#F3F4F6' : 'white',
                  color: currentPage === totalPages ? '#9CA3AF' : '#084734',
                  fontWeight: '800', fontSize: '0.85rem', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s'
                }}
              >
                Next <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: 'white', padding: '4rem 2rem', borderRadius: '20px', border: '1.5px dashed #EEF2E6', textAlign: 'center' }}>
          <Trophy size={48} color="#9CA3AF" style={{ marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#161616', marginBottom: '4px' }}>No Tournaments in this Category</h3>
          <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Select another tab or wait for partner tournament submissions.</p>
        </div>
      )}

      {/* FULL TOURNAMENT & REGISTERED TEAMS DETAIL MODAL FOR ADMIN */}
      {selectedTournament && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(4px)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '750px', width: '100%',
            maxHeight: '92vh', overflowY: 'auto', padding: '2rem', position: 'relative',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s ease-out'
          }}>
            <button
              onClick={() => setSelectedTournament(null)}
              style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: '#F3F4F6', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#374151' }}
            >
              <X size={20} />
            </button>

            {/* Status & Sport Header Badges */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <span style={{
                background: selectedTournament.is_approved ? '#DCEFB8' : '#FEF3C7',
                color: selectedTournament.is_approved ? '#084734' : '#D97706',
                fontSize: '0.78rem', fontWeight: '800', padding: '4px 12px', borderRadius: '20px'
              }}>
                {selectedTournament.is_approved ? '✅ Approved' : '⏳ Pending Approval'}
              </span>
              <span style={{
                background: selectedTournament.is_active ? '#E0F2FE' : '#F3F4F6',
                color: selectedTournament.is_active ? '#0284C7' : '#6B7280',
                fontSize: '0.78rem', fontWeight: '800', padding: '4px 12px', borderRadius: '20px'
              }}>
                {selectedTournament.is_active ? '🟢 Active' : '⏸️ Inactive'}
              </span>
              <span style={{ fontSize: '0.78rem', fontWeight: '800', color: '#084734', background: '#F0FDF4', padding: '4px 12px', borderRadius: '20px' }}>
                {selectedTournament.sport === 'Custom' ? selectedTournament.sport_custom : selectedTournament.sport}
              </span>
            </div>

            {/* Banner Image Preview if uploaded */}
            {selectedTournament.image_url && (
              <img
                src={selectedTournament.image_url}
                alt="Banner"
                style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '16px', marginBottom: '1.25rem' }}
              />
            )}

            <h2 style={{ fontSize: '1.8rem', fontWeight: '900', color: '#0D1F0F', marginBottom: '0.5rem', lineHeight: 1.2 }}>
              {selectedTournament.name}
            </h2>

            {/* STATUS UPDATE CONTROL FOR ADMIN */}
            <div style={{ background: '#F0FDF4', padding: '1rem 1.25rem', borderRadius: '16px', border: '1.5px solid #BBF7D0', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase' }}>Current Status</div>
                <div style={{ fontSize: '1.1rem', fontWeight: '900', color: '#0D1F0F', textTransform: 'capitalize' }}>
                  {selectedTournament.status === 'live' ? '🔴 Live Now' : selectedTournament.status === 'completed' ? '🏁 Completed' : selectedTournament.status === 'cancelled' ? '❌ Cancelled' : '🟢 Upcoming'}
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#374151' }}>Change Status:</span>
                <select
                  value={selectedTournament.status || 'upcoming'}
                  disabled={actionLoading === selectedTournament._id}
                  onChange={e => handleStatusChange(selectedTournament._id, e.target.value, selectedTournament)}
                  style={{ padding: '8px 12px', borderRadius: '10px', border: '1.5px solid #084734', background: '#DCEFB8', color: '#084734', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  <option value="upcoming">🟢 Upcoming</option>
                  <option value="live">🔴 Live Now</option>
                  <option value="completed">🏁 Completed</option>
                  <option value="cancelled">❌ Cancelled</option>
                </select>
              </div>
            </div>

            {/* Partner / Organizer Contact Info Box */}
            <div style={{ background: '#F0FDF4', padding: '1.25rem', borderRadius: '16px', border: '1.5px solid #BBF7D0', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>
                Partner / Organizer Contact Details
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '600' }}>Organizer Name</div>
                  <div style={{ fontWeight: '800', color: '#0D1F0F', fontSize: '0.95rem' }}>
                    {selectedTournament.organizer_name || selectedTournament.owner_name || 'N/A'}
                  </div>
                </div>

                {(selectedTournament.organizer_phone || selectedTournament.owner_phone) && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '600' }}>Phone Number</div>
                    <a
                      href={`tel:${selectedTournament.organizer_phone || selectedTournament.owner_phone}`}
                      style={{ fontWeight: '800', color: '#084734', fontSize: '0.95rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                      <Phone size={14} /> {selectedTournament.organizer_phone || selectedTournament.owner_phone}
                    </a>
                  </div>
                )}

                {selectedTournament.organizer_whatsapp && selectedTournament.organizer_whatsapp.trim() !== '' && selectedTournament.organizer_whatsapp.replace(/\D/g, '').length >= 10 && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '600' }}>WhatsApp</div>
                    <a
                      href={`https://wa.me/${selectedTournament.organizer_whatsapp.replace(/[^\d]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ background: '#25D366', color: 'white', padding: '4px 10px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '800', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}
                    >
                      💬 WhatsApp Chat
                    </a>
                  </div>
                )}

                {selectedTournament.organizer_email && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '600' }}>Email Address</div>
                    <div style={{ fontWeight: '700', color: '#374151', fontSize: '0.85rem' }}>
                      {selectedTournament.organizer_email}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Venue & Event Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem', color: '#4B5563', marginBottom: '1.5rem', background: '#F9FAFB', padding: '1.25rem', borderRadius: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={18} color="#084734" />
                <span><strong>Venue:</strong> {selectedTournament.turf_name} ({selectedTournament.address})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={18} color="#084734" />
                <span><strong>Starts From:</strong> {selectedTournament.start_date ? new Date(selectedTournament.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'TBD'}</span>
              </div>
              {selectedTournament.registration_close_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '26px', color: '#DC2626', fontWeight: '600' }}>
                  <span><strong>Registration Closes:</strong> {new Date(selectedTournament.registration_close_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              {selectedTournament.end_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '26px' }}>
                  <span><strong>Ends On:</strong> {new Date(selectedTournament.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={18} color="#084734" />
                <span><strong>Format:</strong> {selectedTournament.tournament_type === 'Custom' ? selectedTournament.tournament_type_custom : selectedTournament.tournament_type} · {selectedTournament.team_size} Players per team</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Trophy size={18} color="#084734" />
                <span><strong>Entry Fee:</strong> {selectedTournament.entry_fee > 0 ? `₹${selectedTournament.entry_fee} per team` : 'FREE ENTRY'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🎂 <strong>Age Limit:</strong> {selectedTournament.is_open_age || (!selectedTournament.min_age && !selectedTournament.max_age) ? 'Open for All Ages' : `${selectedTournament.min_age || 0} - ${selectedTournament.max_age || 'Any'} Years`}</span>
              </div>
            </div>

            {/* Prize Pool Breakdown */}
            {selectedTournament.prize_pool && (
              <div style={{ background: '#FEF3C7', color: '#D97706', padding: '14px 16px', borderRadius: '16px', fontSize: '0.9rem', fontWeight: '800', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '0.95rem' }}>
                  <Trophy size={20} /> <span>Prize Pool & Rewards Breakdown:</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedTournament.prize_pool.split(' | ').map((pz, idx) => (
                    <span key={idx} style={{ background: 'white', color: '#B45309', padding: '4px 12px', borderRadius: '8px', border: '1px solid #FCD34D', fontSize: '0.82rem', fontWeight: '800', boxShadow: '0 2px 6px rgba(217,119,6,0.1)' }}>
                      {pz}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* REGISTERED TEAMS SECTIONS FOR ADMIN */}
            {(() => {
              const activeRegistrations = registrations.filter(reg =>
                reg.payment_status === 'completed'
              );

              const cancelledRegistrations = registrations.filter(reg =>
                reg.payment_status === 'cancelled' ||
                reg.payment_status === 'refunded' ||
                reg.refund_status === 'refunded'
              );

              return (
                <>
                  {/* SECTION 1: ACTIVE TEAMS */}
                  <div style={{ borderTop: '1px solid #E9EDE8', paddingTop: '1.5rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#0D1F0F', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Users size={20} color="#084734" /> Active Registered Teams ({activeRegistrations.length}{selectedTournament.is_unlimited_teams ? '' : ` / ${selectedTournament.max_teams || '∞'}`})
                      </h4>
                      <span style={{ fontSize: '0.78rem', color: '#084734', background: '#DCEFB8', padding: '4px 10px', borderRadius: '20px', fontWeight: '800' }}>
                        {activeRegistrations.length} Enrolled
                      </span>
                    </div>

                    {loadingRegistrations ? (
                      <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6B7280', fontSize: '0.88rem' }}>
                        Loading registered teams...
                      </div>
                    ) : activeRegistrations.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {activeRegistrations.map((reg, idx) => (
                          <div
                            key={reg._id || idx}
                            style={{
                              background: '#F9FAFB', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '12px 16px',
                              cursor: 'pointer', transition: 'all 0.2s'
                            }}
                            onClick={() => setSelectedTeam(selectedTeam === reg._id ? null : reg._id)}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                              <div>
                                <div style={{ fontWeight: '800', color: '#0D1F0F', fontSize: '0.95rem' }}>
                                  {idx + 1}. {reg.team_name}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#4B5563', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <span>Captain: <strong>{reg.captain_name}</strong></span>
                                  <span>📞 {reg.captain_phone}</span>
                                </div>
                              </div>

                              <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div>
                                  <span style={{ background: '#DCEFB8', color: '#084734', padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800' }}>
                                    ₹{reg.amount_paid} Paid
                                  </span>
                                  <div style={{ fontSize: '0.72rem', color: '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>
                                    {reg.created_at ? new Date(reg.created_at).toLocaleDateString('en-IN') : ''}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {selectedTeam === reg._id && (
                              <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #D1D5DB', fontSize: '0.85rem', color: '#374151', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', background: 'white', padding: '10px 12px', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
                                  <div><strong>Captain Email:</strong> {reg.captain_email || 'N/A'}</div>
                                  <div><strong>City / Location:</strong> {reg.city || 'N/A'}</div>
                                  <div><strong>Primary Contact:</strong> 📞 {reg.captain_phone}</div>
                                  <div><strong>Alternate Phone:</strong> {reg.alternate_phone ? `📞 ${reg.alternate_phone}` : 'N/A'}</div>
                                  <div><strong>Player Count:</strong> {reg.player_count} Players</div>
                                  <div><strong>Payment Status:</strong> <span style={{ color: '#16a34a', fontWeight: '800' }}>{reg.payment_status?.toUpperCase()}</span></div>
                                  <div><strong>PayU ID (mihpayid):</strong> {reg.payu_mihpayid || 'N/A'}</div>
                                  <div><strong>Bank Ref / RRN:</strong> {reg.bank_ref_num || 'N/A'}</div>
                                  <div><strong>Payment Mode / UPI:</strong> {reg.upi_id ? `📱 ${reg.upi_id}` : (reg.payment_mode || 'PayU')}</div>
                                </div>
                                {reg.additional_contact && (
                                  <div style={{ background: '#FFFBEB', padding: '8px 12px', borderRadius: '8px', border: '1px solid #FCD34D', color: '#B45309' }}>
                                    <strong>Additional Contact / Notes:</strong> {reg.additional_contact}
                                  </div>
                                )}
                                {reg.player_names && (
                                  <div style={{ background: 'white', padding: '10px 12px', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
                                    <strong>Roster / Player Names:</strong>
                                    <p style={{ margin: '4px 0 0', color: '#4B5563', whiteSpace: 'pre-line' }}>{reg.player_names}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ background: '#F9FAFB', padding: '1.5rem', borderRadius: '14px', border: '1px dashed #D1D5DB', textAlign: 'center', color: '#6B7280', fontSize: '0.85rem' }}>
                        No active team registrations for this tournament.
                      </div>
                    )}
                  </div>

                  {/* SECTION 2: SEPARATE SECTION FOR CANCELLED & REFUNDED TEAMS FOR ADMIN */}
                  <div style={{ borderTop: '1.5px dashed #FCA5A5', paddingTop: '1.5rem', marginBottom: '1.5rem', background: '#FEF2F2', padding: '1.25rem', borderRadius: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#991B1B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <XCircle size={20} color="#DC2626" /> Cancelled & Refunded Teams ({cancelledRegistrations.length})
                      </h4>
                      <span style={{ fontSize: '0.78rem', color: '#991B1B', background: '#FEE2E2', padding: '4px 10px', borderRadius: '20px', fontWeight: '800' }}>
                        {cancelledRegistrations.length} Cancelled
                      </span>
                    </div>

                    {cancelledRegistrations.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {cancelledRegistrations.map((reg, idx) => (
                          <div
                            key={reg._id || idx}
                            style={{
                              background: 'white', borderRadius: '14px', border: '1.5px solid #FCA5A5', padding: '12px 16px',
                              cursor: 'pointer', transition: 'all 0.2s'
                            }}
                            onClick={() => setSelectedTeam(selectedTeam === reg._id ? null : reg._id)}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                              <div>
                                <div style={{ fontWeight: '800', color: '#991B1B', fontSize: '0.95rem' }}>
                                  {idx + 1}. {reg.team_name} <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#DC2626' }}>(Cancelled)</span>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#4B5563', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <span>Captain: <strong>{reg.captain_name}</strong></span>
                                  <span>📞 {reg.captain_phone}</span>
                                </div>
                              </div>

                              <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div>
                                  {reg.payment_status === 'refunded' || reg.refund_status === 'refunded' ? (
                                    <span style={{ background: '#DCFCE7', color: '#15803D', border: '1px solid #86EFAC', padding: '4px 10px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '800' }}>
                                      ✅ Refunded (₹{reg.refund_amount || reg.amount_paid})
                                    </span>
                                  ) : (
                                    <>
                                      <span style={{ background: '#FEE2E2', color: '#DC2626', padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800' }}>
                                        ❌ Cancelled (₹{reg.amount_paid})
                                      </span>
                                      {reg.amount_paid > 0 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAdminRefund(reg._id || reg.id, reg.team_name, reg.amount_paid);
                                          }}
                                          disabled={refundingId === (reg._id || reg.id)}
                                          style={{
                                            background: '#DC2626',
                                            color: '#FFFFFF',
                                            border: 'none',
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            fontSize: '0.78rem',
                                            fontWeight: '800',
                                            cursor: refundingId === (reg._id || reg.id) ? 'wait' : 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            marginLeft: '6px'
                                          }}
                                          title="Process refund for this cancelled team"
                                        >
                                          💸 {refundingId === (reg._id || reg.id) ? 'Refunding...' : 'Refund Team'}
                                        </button>
                                      )}
                                    </>
                                  )}
                                  <div style={{ fontSize: '0.72rem', color: '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>
                                    {reg.created_at ? new Date(reg.created_at).toLocaleDateString('en-IN') : ''}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {selectedTeam === reg._id && (
                              <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #FCA5A5', fontSize: '0.85rem', color: '#374151', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', background: '#FFF5F5', padding: '10px 12px', borderRadius: '10px', border: '1px solid #FECACA' }}>
                                  <div><strong>Captain Email:</strong> {reg.captain_email || 'N/A'}</div>
                                  <div><strong>City / Location:</strong> {reg.city || 'N/A'}</div>
                                  <div><strong>Primary Contact:</strong> 📞 {reg.captain_phone}</div>
                                  <div><strong>Alternate Phone:</strong> {reg.alternate_phone ? `📞 ${reg.alternate_phone}` : 'N/A'}</div>
                                  <div><strong>Player Count:</strong> {reg.player_count} Players</div>
                                  <div><strong>Payment Status:</strong> <span style={{ color: '#DC2626', fontWeight: '800' }}>{reg.payment_status?.toUpperCase()}</span></div>
                                  <div><strong>Refund Status:</strong> <span style={{ color: reg.refund_status === 'refunded' ? '#16a34a' : '#D97706', fontWeight: '800' }}>{reg.refund_status?.toUpperCase() || 'N/A'}</span></div>
                                  <div><strong>PayU ID (mihpayid):</strong> {reg.payu_mihpayid || 'N/A'}</div>
                                </div>
                                {reg.player_names && (
                                  <div style={{ background: 'white', padding: '10px 12px', borderRadius: '10px', border: '1px solid #FECACA' }}>
                                    <strong>Roster / Player Names:</strong>
                                    <p style={{ margin: '4px 0 0', color: '#4B5563', whiteSpace: 'pre-line' }}>{reg.player_names}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ background: 'white', padding: '1rem', borderRadius: '12px', border: '1px dashed #FECACA', textAlign: 'center', color: '#9CA3AF', fontSize: '0.82rem' }}>
                        No cancelled or refunded teams for this tournament.
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            {/* Leaderboard for completed tournaments */}
            {selectedTournament.status === 'completed' && (
              <div style={{ borderTop: '1px solid #E9EDE8', paddingTop: '1.5rem', marginBottom: '1rem' }}>
                <TournamentLeaderboard tournamentId={selectedTournament._id} token={token} />
              </div>
            )}

            {/* Admin Action Bar */}
            <div style={{ borderTop: '1px solid #E9EDE8', paddingTop: '1.25rem', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {!selectedTournament.is_approved ? (
                <button
                  onClick={() => handleApprove(selectedTournament._id, true)}
                  disabled={actionLoading === selectedTournament._id}
                  style={{
                    background: '#084734', color: '#CEF17B', border: 'none',
                    padding: '10px 20px', borderRadius: '12px', fontWeight: '800',
                    fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  <CheckCircle size={16} /> Approve Tournament
                </button>
              ) : (
                <button
                  onClick={() => handleApprove(selectedTournament._id, false)}
                  disabled={actionLoading === selectedTournament._id}
                  style={{
                    background: '#FEF3C7', color: '#D97706', border: '1px solid #FCD34D',
                    padding: '10px 20px', borderRadius: '12px', fontWeight: '700',
                    fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  <XCircle size={16} /> Revoke Approval
                </button>
              )}

              <button
                onClick={() => handleToggleActive(selectedTournament)}
                disabled={actionLoading === selectedTournament._id}
                style={{
                  background: selectedTournament.is_active ? '#F3F4F6' : '#E0F2FE',
                  color: selectedTournament.is_active ? '#374151' : '#0284C7',
                  border: '1px solid #E5E7EB',
                  padding: '10px 20px', borderRadius: '12px', fontWeight: '700',
                  fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                <Power size={16} /> {selectedTournament.is_active ? 'Deactivate' : 'Activate'}
              </button>

              <button
                onClick={() => handleDelete(selectedTournament._id)}
                disabled={actionLoading === selectedTournament._id}
                style={{
                  background: '#FEE2E2', color: '#DC2626', border: 'none',
                  padding: '10px 20px', borderRadius: '12px', fontWeight: '700',
                  fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
